import { AbortMultipartUploadCommand, CompleteMultipartUploadCommand, CreateMultipartUploadCommand, DeleteObjectCommand, HeadObjectCommand, ListPartsCommand, PutObjectCommand, S3Client, UploadPartCommand } from '@aws-sdk/client-s3';
import { logEvent } from 'components/telemetry/index';
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { getContentStorageUrl } from 'helpers';
import { triggerFunction } from 'services/api/functions';
import { Article, ContentType, Edition } from 'types';
import firebase, { firebaseConfig } from './initconfig';
import { setRecord } from './index';
import type { MultipartPart, MultipartTransport } from 'video/multipartUpload';
import { UploadTransportError } from 'video/uploadErrors';
import type { UploadStage } from 'video/uploadErrors';

let S3: S3Client;
const initR2 = async (refresh = false) => {
	if (!S3 || refresh) {
		let R2Config: any = refresh ? null : localStorage.getItem('R2Config');
		if (!R2Config) {
			R2Config = await triggerFunction('getR2Meta', {});
			localStorage.setItem('R2Config', JSON.stringify(R2Config));
		} else {
			R2Config = JSON.parse(R2Config);
		}

		S3 = new S3Client(R2Config);
	}
};

export const refreshR2Configuration = async () => {
	try {
		await initR2(true);
	} catch (cause) {
		throw new UploadTransportError('initialize', cause);
	}
};

const withR2 = async <T>(stage: UploadStage, action: (client: S3Client) => Promise<T>, signal?: AbortSignal): Promise<T> => {
	const checkCancellation = () => {
		if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
	};
	const fail = (operation: UploadStage, cause: unknown): never => {
		if (cause instanceof Error && cause.name === 'AbortError') throw cause;
		checkCancellation();
		if (cause instanceof UploadTransportError) throw cause;
		throw new UploadTransportError(operation, cause);
	};
	checkCancellation();
	try {
		await initR2();
		checkCancellation();
	} catch (cause) {
		return fail('initialize', cause);
	}
	try {
		const result = await action(S3);
		checkCancellation();
		return result;
	} catch (cause) {
		return fail(stage, cause);
	}
};

export const r2MultipartTransport: MultipartTransport = {
	async create(key, mime, signal) {
		return withR2('create', async client => {
			const result = await client.send(new CreateMultipartUploadCommand({
				Bucket: 'appmedia', Key: key, ContentType: mime, CacheControl: 'public, max-age=31536000',
			}), { abortSignal: signal });
			if (!result.UploadId) throw new Error('R2 did not return an upload session ID.');
			return result.UploadId;
		}, signal);
	},
	async uploadPart(key, uploadId, partNumber, body, signal) {
		return withR2('part', async client => {
			const result = await client.send(new UploadPartCommand({
				Bucket: 'appmedia', Key: key, UploadId: uploadId, PartNumber: partNumber, Body: body,
			}), { abortSignal: signal });
			if (result.ETag?.trim()) return result.ETag;
			const parts = await r2MultipartTransport.listParts(key, uploadId, signal);
			const part = parts.find(part => part.partNumber === partNumber);
			if (!part || !part.etag.trim()) {
				throw new Error(`R2 did not return a part ETag, and ListParts did not confirm an ETag for part ${partNumber}.`);
			}
			if (part.size !== body.size) {
				throw new Error(`R2 ListParts reported an unexpected size for part ${partNumber}: expected ${body.size} bytes, received ${part.size}.`);
			}
			return part.etag;
		}, signal);
	},
	async listParts(key, uploadId, signal) {
		return withR2('list', async client => {
			const parts: MultipartPart[] = [];
			let marker: string | undefined;
			do {
				const result = await client.send(new ListPartsCommand({
					Bucket: 'appmedia', Key: key, UploadId: uploadId, PartNumberMarker: marker,
				}), { abortSignal: signal });
				for (const part of result.Parts ?? []) {
					if (!part.PartNumber || !part.ETag || part.Size === undefined) throw new Error('R2 returned incomplete upload part metadata.');
					parts.push({ partNumber: part.PartNumber, etag: part.ETag, size: part.Size });
				}
				marker = result.IsTruncated ? result.NextPartNumberMarker : undefined;
				if (result.IsTruncated && !marker) throw new Error('R2 returned an invalid multipart continuation.');
			} while (marker);
			return parts;
		}, signal);
	},
	async complete(key, uploadId, parts, signal) {
		return withR2('complete', async client => {
			await client.send(new CompleteMultipartUploadCommand({
				Bucket: 'appmedia', Key: key, UploadId: uploadId,
				MultipartUpload: { Parts: parts.map((part) => ({ PartNumber: part.partNumber, ETag: part.etag })) },
			}), { abortSignal: signal });
		}, signal);
	},
	async abort(key, uploadId) {
		return withR2('abort', async client => {
			await client.send(new AbortMultipartUploadCommand({ Bucket: 'appmedia', Key: key, UploadId: uploadId }));
		});
	},
	async head(key, signal) {
		return withR2('verify', async client => {
			const result = await client.send(new HeadObjectCommand({ Bucket: 'appmedia', Key: key }), { abortSignal: signal });
			if (result.ContentLength === undefined) throw new Error('R2 did not return the completed video size.');
			return { size: result.ContentLength };
		}, signal);
	},
};

export const uploadMediaFileToR2 = async (article: Article) => {
	return new Promise(async (resolve, reject) => {
		try {
			if (article.mediaUriR2) {
				console.log('Already present in R2', article);
				reject(false);
				return;
			}

			await initR2();

			let path = 'appstatic/media/' + article.mediaUri;
			let uri = getContentStorageUrl(ContentType.Article, article.articleType, article);

			uri = uri.replace('https://appmedia.atlotusfeet.com/', 'https://storage.googleapis.com/appmedia.atlotusfeet.com/');

			console.log('Original Uri: ', path, uri);
			let fileTitle = article.title?.en ?? article.title?.hi ?? article.title ?? '';
			fileTitle = fileTitle.replace('–', '-').replace('’', "'");
			console.log('fileTitle: ' + fileTitle);

			let result = await window.fetch(uri);
			let file = await result.blob();

			let putObj: any = {
				Bucket: 'appmedia',
				ACL: 'public-read',
				Body: file,
				Key: path,
				ContentType: file.type,
				CacheControl: 'public, max-age=31536000',
				Metadata: {
					filetitle: fileTitle,
				},
			};

			let r2Task = await S3.send(new PutObjectCommand(putObj));

			console.log('Media Uploaded to R2: ', putObj);
			await setRecord('articles', article.id, { mediaUriR2: article.mediaUri });
			console.log('Article record updated', article);
			resolve(true);
		} catch (error) {
			console.error(error);
			logEvent('storage_upload_error', error);
			reject(error);
		}
	});
};

export const uploadSnapshotFileToR2 = async () => {
	return new Promise(async (resolve, reject) => {
		try {
			await initR2();

			let name = 'WebSnapshot - 1723561491620.json';
			let path = 'appstatic/data/' + name;
			let uri = getContentStorageUrl(ContentType.Data, name);

			console.log('Original Uri: ', path, uri);

			// try {
			// 	let r2Media = await S3.send(
			// 		new HeadObjectCommand({
			// 			Bucket: 'appmedia',
			// 			Key: path,
			// 		})
			// 	);

			// 	if (r2Media) {
			// 		console.log('uploadMediaFileToR2: Skipping, Media Found', path, r2Media, edition);
			// 		resolve(true);
			// 		return;
			// 	}
			// } catch (error) {
			// 	console.log('uploadMediaFileToR2: Continuing, Media Not Found', path);
			// }

			let result = await window.fetch(uri);
			let file = await result.blob();

			let putObj: any = {
				Bucket: 'appmedia',
				ACL: 'public-read',
				Body: file,
				Key: path,
				ContentType: file.type,
				CacheControl: 'public, max-age=31536000',
			};

			let r2Task = await S3.send(new PutObjectCommand(putObj));

			console.log('Media Uploaded to R2: ', putObj);
			resolve(true);
		} catch (error) {
			console.error(error);
			logEvent('storage_upload_error', error);
			reject(error);
		}
	});
};

export const uploadBlobToR2 = async (path: string, file: string, type: string) => {
	try {
		await initR2();

		await S3.send(
			new PutObjectCommand({
				Bucket: 'appmedia',
				ACL: 'public-read',
				Body: file,
				Key: path,
				ContentType: type,
				CacheControl: 'public, max-age=31536000',
			})
		);
	} catch (error) {
		logEvent('storage_upload_error', { path, ...error });
	}
};

export const uploadFileToR2 = async (path: string, file: File, signal?: AbortSignal) => {
	try {
		if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
		await initR2();
		if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
		await S3.send(
			new PutObjectCommand({
				Bucket: 'appmedia',
				Key: path,
				Body: file,
				ContentType: file.type,
				CacheControl: 'public, max-age=31536000',
			}),
			{ abortSignal: signal }
		);
		if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
		logEvent('storage_upload', { path, totalBytes: file.size, storage: 'r2' });
	} catch (error) {
		logEvent('storage_upload_error', { path, storage: 'r2', ...error });
		throw error;
	}
};

export const deleteFileFromR2 = async (path: string) => {
	try {
		await initR2();
		await S3.send(new DeleteObjectCommand({ Bucket: 'appmedia', Key: path }));
		logEvent('storage_delete', { path, storage: 'r2' });
	} catch (error) {
		logEvent('storage_delete_error', { path, storage: 'r2', ...error });
		throw error;
	}
};

export const uploadFile = async (
	path: string,
	file: File,
	progressCallback?: (progress: number, bytesTransferred?: number, totalBytes?: number) => void,
	uploadOnR2: boolean = true,
	fileTitle?: string,
	signal?: AbortSignal
) => {
	return new Promise(async (resolve, reject) => {
		try {
			if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
			let r2Task;
			if (uploadOnR2) {
				await initR2();
				if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');

				console.log('UploadFile: ' + path);
				r2Task = S3.send(
					new PutObjectCommand({
						Bucket: 'appmedia',
						ACL: 'public-read',
						Body: file,
						Key: path,
						ContentType: file.type,
						CacheControl: 'public, max-age=31536000',
						Metadata: {
							filetitle: fileTitle ?? '',
						},
					}),
					{ abortSignal: signal }
				);
			}

			var storage = getStorage(firebase);
			var fileRef = ref(storage, path);

			var metadata = {
				contentType: file.type,
				cacheControl: 'public, max-age=31536000',
			};

			let uploadTask = uploadBytesResumable(fileRef, file, metadata);
			const cancel = () => uploadTask.cancel();
			signal?.addEventListener('abort', cancel, { once: true });
			if (signal?.aborted) cancel();
			r2Task?.catch((error) => {
				cancel();
				logEvent('storage_upload_error', { path, ...error });
				reject(error);
			});
			uploadTask.on(
				'state_changed',
				function (snapshot) {
					var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
					progressCallback && progressCallback(progress, snapshot.bytesTransferred, snapshot.totalBytes);
				},
				function (error: any) {
					signal?.removeEventListener('abort', cancel);
					switch (error.code) {
						case 'storage/unauthorized':
							// User doesn't have permission to access the object
							break;

						case 'storage/canceled':
							// User canceled the upload
							break;

						case 'storage/unknown':
							// Unknown error occurred, inspect error.serverResponse
							break;
					}

					logEvent('storage_upload_error', { path, ...error });
					reject(error);
				},
				async function () {
					signal?.removeEventListener('abort', cancel);
					try {
						await r2Task;
						if (signal?.aborted) throw new DOMException('Upload cancelled.', 'AbortError');
						logEvent('storage_upload', { path, totalBytes: file.size });
						resolve(100);
					} catch (error) {
						logEvent('storage_upload_error', { path, ...error });
						reject(error);
					}
				}
			);
		} catch (error) {
			logEvent('storage_upload_error', { path, ...error });
			reject(error);
		}
	});
};

export const deleteFile = async (path: string) => {
	try {
		console.log('DeleteFile: ' + path);

		// let r2Task = await S3.send(
		// 	new DeleteObjectCommand({
		// 		Bucket: 'appmedia',
		// 		Key: path,
		// 	})
		// );

		var storage = getStorage(firebase);
		var pathReference = ref(storage, path);

		await deleteObject(pathReference);
		logEvent('storage_delete', { path });
	} catch (error) {
		logEvent('storage_delete_error', { path, ...error });
		throw error;
	}
};

export const getDownloadUrl = async (path: string) => {
	var storage = getStorage(firebase);
	var pathReference = ref(storage, path);

	try {
		let downloadUrl = await getDownloadURL(pathReference);
		return downloadUrl;
	} catch (error) {
		// A full list of error codes is available at
		// https://firebase.google.com/docs/storage/web/handle-errors
		switch (error.code) {
			case 'storage/object-not-found':
				// File doesn't exist
				break;

			case 'storage/unauthorized':
				// User doesn't have permission to access the object
				break;

			case 'storage/canceled':
				// User canceled the upload
				break;

			case 'storage/unknown':
				// Unknown error occurred, inspect the server response
				break;
		}
		logEvent('storage_download_error', { path, ...error });
	}
};

export const getStorageUrl = (path: string) => {
	path = path.replace(/\//g, '%2F');

	return 'https://firebasestorage.googleapis.com/v0/b/' + firebaseConfig.storageBucket + '/o/' + path + '?alt=media';
};
